import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
} from '@angular/core';
import { IconoirWarningCircle } from '@ey-xd/motif-icon';
import { MotifModule } from "@ey-xd/motif-wc-angular"; 
import { AgentFiveService } from 'app/core/service/agent-five/agent-five.service';
import { ToastService } from 'app/core/service/toast/toast.service';
import { CheckCard } from 'app/models/check-card';
import { take, tap } from 'rxjs';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ApiFile } from 'app/models/api-response';

@Component({
  selector: 'app-agent-five-home-cards',
  standalone: true,
  imports: [
    MotifModule,
  ],
  templateUrl: './agent-five-home-cards.component.html',
  styleUrl: './agent-five-home-cards.component.scss',
})
export class AgentFiveHomeCardsComponent {
  @Output() changeToTabEmitter = new EventEmitter<number>();
  @Input() filesToRun: any[] = [];

  // emitters to avoid warnings for doc uploader component
  @Output() showWarningForFSChecks = new EventEmitter<boolean>(false);
  @Output() showWarningForFSvsComp = new EventEmitter<boolean>(false);
  @Output() showWarningForCYvsPY = new EventEmitter<boolean>(false);

  // local variables to track emitters so we display the toast
  warningForFSChecks: boolean = false;
  warningForFSvsComp: boolean = false;
  warningForCYvsPY: boolean = false;

  // this is to execute after the isThereALatestRun is completed in all the checks
  latestRunCheckCompleted: boolean = false;

  IconoirWarningCircle = IconoirWarningCircle;

  checksArray: CheckCard[] = [
    {
      checkNumber: 1,
      name: 'Financial Statement Review',
      description:
        'The Financial Statement Review will highlight potentially tax sensitve items in the Financial Statements. To proceed please upload the current year Financial Statements above and click "Run".',
      tabNumberOrder: 2,
      disabled: true,
      runLoading: false, // for when the run is loading
      errorMessage: '',
      reportId: undefined,
      reportStatus: 0,
      darkenCardContent: false,
      latestRunCheckCompleted: false,
    },
    {
      checkNumber: 2,
      name: 'Financial Statement to Computation comparison',
      description:
        'The Financial Statement to Computation comparison will compare items and values in both documents and highlight potential discrepancies. To proceed please upload the Financial Statements and Current Year Computation and return above and click "Run"',
      tabNumberOrder: 3,
      disabled: true,
      runLoading: false,
      errorMessage: '',
      reportId: undefined,
      reportStatus: 0,
      darkenCardContent: false,
      latestRunCheckCompleted: false,
    },
    {
      checkNumber: 3,
      name: 'Current Year to Prior Year Computation comparison',
      description:
        'The Current Year to Prior Year Computation comparison will compare items and values in both documents and highlight potential discrepanices. To proceed please upload the current draft of the Current Year Computation and the Prior Year submitted Computation and click "Run".',
      tabNumberOrder: 4,
      disabled: true,
      runLoading: false,
      errorMessage: '',
      reportId: undefined,
      reportStatus: 0,
      darkenCardContent: false,
      latestRunCheckCompleted: false,
    },
  ];

  // Zoom related properties for breakpoint observer
  isZoomed: boolean = false;

  // array of retrieved files from past
  // processes that we pass to parent component if they exist
  retrievedFiles: ApiFile[] = [];
  @Output() retrievedFilesEmitter = new EventEmitter<ApiFile[]>();

  constructor(
    private agentFiveService: AgentFiveService,
    private toastService: ToastService,
    private breakpointObserver: BreakpointObserver
  ) {
    this.breakpointObserver
      .observe([Breakpoints.Handset, '(max-width: 900px)'])
      .subscribe((result) => {
        // result matches returns true if the width is <= 900px
        this.isZoomed = result.matches;
      });
  }

  ngOnInit() {
    this.checksArray.forEach((check: CheckCard) => {
      const storedCheck = '';
      if (storedCheck) {
        const ch = JSON.parse(storedCheck);
        check.runLoading = ch.runLoading;
      }
      this.isThereALatestRun(check);
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes && changes['filesToRun']) {
      this.checksArray.forEach((check) => {
        if (check.errorMessage !== '') {
          this.checkErrorMessageOnUpload(check);
        }
      });
    }
  }

  changeToTab(tabNumberOrder: number) {
    this.toastService.clearToastWithTag(true);

    this.changeToTabEmitter.emit(tabNumberOrder);
  }

  /**
   * Check if there is a "latest" run for the respective check and handle "disable" to go to that latest run
   *
   */
  private isThereALatestRun(check: CheckCard) {
    switch (check.checkNumber) {
      case 1:
        // FS Check
        this.agentFiveService
          .getFSCheckLatestId()
          .pipe(take(1))
          .subscribe({
            next: (data: any) => {
              // we apply the needed variables for checks before proceeding
              check.reportId = this.getLatestProjectId(data);
              check.reportStatus = this.getLatestProjectStatus(data);
              const storedCheck = '';

              if (storedCheck && check.reportId === undefined) {
                check.runLoading = false;
                this.toastService.showToast(
                  'error',
                  `The process for ${check.name} you were running did not generate a report. Please run the process again. If the error persist, please contact servicedesk@ey.com`,
                  {
                    classTheme: '',
                    autoHide: false,
                  }
                );
                return; // The process did not generate a report, exit.
              }

              // file retrieval
              const files: ApiFile[] = this.getFiles(data);
              this.manageFilesToRetrieve(files);

              // logic for checking the status and retrying
              if (check.reportStatus === 30) {
                // 30 is success
                check.disabled = false;
                check.runLoading = false;

                // emit to doc uploader component that we have something so it can show the warning
                this.showWarningForFSChecks.emit(true);
                this.warningForFSChecks = true;

                const storedCheck = '';
                if (storedCheck) {
                  this.toastService.showToast(
                    'success',
                    `${check.name} process is completed. View last run for more details.`,
                    {
                      classTheme: '',
                      autoHide: false,
                    }
                  );
                }
              } else if (
                check.reportStatus === 10 ||
                check.reportStatus === 20
              ) {
                // 10 & 20 are running
                setTimeout(() => {
                  this.isThereALatestRun(check);
                }, 30000);
              } else {
                // 40 or others are Failures
                check.disabled = true;
                check.runLoading = false;
                check.reportId = undefined; // to ensure that we don't pick up the reportId if the status is a failure

                const storedCheck = '';
                if (storedCheck) {
                  this.toastService.showToast(
                    'error',
                    `${check.name} process failed. Run the process again. If the error persist, please contact servicedesk@ey.com`,
                    {
                      classTheme: '',
                      autoHide: false,
                    }
                  );
                }
              }

              check.latestRunCheckCompleted = true;
            },
            error: (error: HttpErrorResponse) => {
              const storedCheck = '';

              // turn the spinners back to normal
              check.disabled = true;
              check.runLoading = false;

              // in case we have the stored check (is running) and it fails, display the message
              // and remove the item from local storage
              if (storedCheck) {
                this.toastService.showHttpResponseErrorMessage(error, '');
              }

              check.latestRunCheckCompleted = true;
            },
          });
        break;
      case 2:
        // FS vs Comp
        this.agentFiveService
          .getFSvsCompLatestId()
          .pipe(take(1))
          .subscribe({
            next: (data: any) => {
              check.reportId = this.getLatestProjectId(data);
              check.reportStatus = this.getLatestProjectStatus(data);
              const storedCheck = '';

              if (storedCheck && check.reportId === undefined) {
                check.runLoading = false;
                this.toastService.showToast(
                  'error',
                  `The process for ${check.name} you were running did not generate a report. Please run the process again. If the error persist, please contact servicedesk@ey.com`,
                  {
                    classTheme: '',
                    autoHide: false,
                  }
                );
                return; // The process did not generate a report, exit.
              }

              // file retrieval
              const files: ApiFile[] = this.getFiles(data);
              this.manageFilesToRetrieve(files);
              // logic for checking the status and retrying
              if (check.reportStatus === 30) {
                // 30 is success
                check.disabled = false;
                check.runLoading = false;

                // emit to doc uploader component that we have something so it can show the warning
                this.showWarningForFSvsComp.emit(true);
                this.warningForFSvsComp = true;

                const storedCheck = '';
                if (storedCheck) {
                  this.toastService.showToast(
                    'success',
                    `${check.name} process is completed. View last run for more details.`,
                    {
                      classTheme: '',
                      autoHide: false,
                    }
                  );
                }
              } else if (
                check.reportStatus === 10 ||
                check.reportStatus === 20
              ) {
                // 10 & 20 are running
                setTimeout(() => {
                  this.isThereALatestRun(check);
                }, 30000);
              } else {
                // 40 or others are Failures
                check.disabled = true;
                check.runLoading = false;
                check.reportId = undefined; // to ensure that we don't pick up the reportId if the status is a failure

                const storedCheck = '';
                if (storedCheck) {
                  this.toastService.showToast(
                    'error',
                    `${check.name} process failed. Run the process again. If the error persist, please contact servicedesk@ey.com`,
                    {
                      classTheme: '',
                      autoHide: false,
                    }
                  );
                }
              }

              check.latestRunCheckCompleted = true;
            },
            error: (error: HttpErrorResponse) => {
              const storedCheck = '';

              // turn the spinners back to normal
              check.disabled = true;
              check.runLoading = false;

              // in case we have the stored check (is running) and it fails, display the message
              // and remove the item from local storage
              if (storedCheck) {
                const message: string = error.error.message || '';
                this.toastService.showToast('error', `${message}`, {
                  classTheme: '',
                  autoHide: false,
                });
              }

              check.latestRunCheckCompleted = true;
            },
          });
        break;
      case 3:
        // CY vs PY Comp
        this.agentFiveService
          .getCYvsPYLatestId()
          .pipe(take(1))
          .subscribe({
            next: (data: any) => {
              check.reportId = this.getLatestProjectId(data);
              check.reportStatus = this.getLatestProjectStatus(data);
              const storedCheck = '';

              if (storedCheck && check.reportId === undefined) {
                check.runLoading = false;
                this.toastService.showToast(
                  'error',
                  `The process for ${check.name} you were running did not generate a report. Please run the process again. If the error persist, please contact servicedesk@ey.com`,
                  {
                    classTheme: '',
                    autoHide: false,
                  }
                );
                return; // The process did not generate a report, exit.
              }

              // file retrieval
              const files: ApiFile[] = this.getFiles(data);
              this.manageFilesToRetrieve(files);
              // logic for checking the status and retrying
              if (check.reportStatus === 30) {
                // 30 is success
                check.disabled = false;
                check.runLoading = false;

                // emit to doc uploader component that we have something so it can show the warning
                this.showWarningForCYvsPY.emit(true);
                this.warningForCYvsPY = true;

                const storedCheck = '';
                if (storedCheck) {
                  this.toastService.showToast(
                    'success',
                    `${check.name} process is completed. View last run for more details.`,
                    {
                      classTheme: '',
                      autoHide: false,
                    }
                  );
                }
              } else if (
                check.reportStatus === 10 ||
                check.reportStatus === 20
              ) {
                // 10 & 20 are running
                setTimeout(() => {
                  this.isThereALatestRun(check);
                }, 30000);
              } else {
                // 40 or others are Failures
                check.disabled = true;
                check.runLoading = false;
                check.reportId = undefined; // to ensure that we don't pick up the reportId if the status is a failure

                const storedCheck = '';
                if (storedCheck) {
                  this.toastService.showToast(
                    'error',
                    `${check.name} process failed. Run the process again. If the error persist, please contact servicedesk@ey.com`,
                    {
                      classTheme: '',
                      autoHide: false,
                    }
                  );
                }
              }

              check.latestRunCheckCompleted = true;
            },
            error: (error: HttpErrorResponse) => {
              const storedCheck = '';

              // turn the spinners back to normal
              check.disabled = true;
              check.runLoading = false;

              // in case we have the stored check (is running) and it fails, display the message
              // and remove the item from local storage
              if (storedCheck) {
                const message: string = error.error.message || '';
                this.toastService.showToast('error', `${message}`, {
                  classTheme: '',
                  autoHide: false,
                });
              }

              check.latestRunCheckCompleted = true;
            },
          });
        break;
    }
  }

  runCheck(checkNumber: number) {
    this.toastService.clearToastWithTag(true);

    switch (checkNumber) {
      case 1: {
        //FS Check
        const check: CheckCard | any = this.checksArray.find(
          (check) => check.checkNumber === checkNumber
        );
        if (check) {
          check.runLoading = true;
          check.disabled = true;
        }

        const fileToRun = this.filesToRun.find(
          (document) => document.documentNumber === 1
        )?.file;

        this.agentFiveService
          .postDocumentToFSChecks(fileToRun)
          .pipe(
            take(1),
            tap((_) => {
              // activate the button for the check after posting the document
              if (check) {
                const ch = {
                  name: check.name,
                  checkNumber: check.checkNumber,
                  runLoading: true,
                };
                this.isThereALatestRun(check);
              }
            })
          )
          .subscribe({
            next: (data: any) => {
              check.errorMessage = '';
              this.toastService.showToast(
                'success',
                `${check.name} processing initialized successfully.`,
                {
                  classTheme: '',
                  autoHide: false,
                }
              );
              setTimeout(() => {
                this.isThereALatestRun(check);
              }, 30000);
            },
            error: (error: HttpErrorResponse) => {
              if (check.reportId !== undefined) {
                check.disabled = false;
              }
              check.runLoading = false;

              if (!fileToRun) {
                check.errorMessage = `Test failed: Missing Current Year Financial Statements file.`;
              }
              if (fileToRun) {
                this.toastService.showHttpResponseErrorMessage(error);
              }
            },
          });
        break;
      }
      case 2: {
        // FS vs Comp
        const check: CheckCard | any = this.checksArray.find(
          (check) => check.checkNumber === checkNumber
        );
        if (check) {
          check.runLoading = true;
          check.disabled = true;
        }

        const fileToRun1 = this.filesToRun.find(
          (document) => document.documentNumber === 1
        )?.file;
        const fileToRun2 = this.filesToRun.find(
          (document) => document.documentNumber === 3
        )?.file;

        this.agentFiveService
          .postDocumentsToFSvsComp([fileToRun1, fileToRun2])
          .pipe(
            take(1),
            tap((_) => {
              // activate the button for the check after posting the document
              if (check) {
                const ch = {
                  name: check.name,
                  checkNumber: check.checkNumber,
                  runLoading: true,
                };
                this.isThereALatestRun(check);
              }
            })
          )
          .subscribe({
            next: (data: any) => {
              check.errorMessage = '';
              this.toastService.showToast(
                'success',
                `${check.name} processing initialized successfully.`,
                {
                  classTheme: '',
                  autoHide: false,
                }
              );
              setTimeout(() => {
                this.isThereALatestRun(check);
              }, 30000);
            },
            error: (error: HttpErrorResponse) => {
              if (check.reportId !== undefined) {
                check.disabled = false;
              }
              check.runLoading = false;

              if (!fileToRun1 && !fileToRun2) {
                check.errorMessage = `Test failed: Missing Current Year Financial Statements and Current Year Tax Computation files.`;
              } else if (!fileToRun1 && fileToRun2) {
                check.errorMessage = `Test failed: Missing Current Year Financial Statements file.`;
              } else if (fileToRun1 && !fileToRun2) {
                check.errorMessage = `Test failed: Missing Current Year Tax Computation file.`;
              } else if (fileToRun1 && fileToRun2) {
                const message: string = error.error.message;
                this.toastService.showToast('error', `${message}`, {
                  classTheme: '',
                  autoHide: false,
                });
              }
            },
          });
        break;
      }
      case 3: {
        // CY vs PY Comp
        const check: CheckCard | any = this.checksArray.find(
          (check) => check.checkNumber === checkNumber
        );
        if (check) {
          check.runLoading = true;
          check.disabled = true;
        }

        const fileToRun1 = this.filesToRun.find(
          (document) => document.documentNumber === 3
        )?.file;
        const fileToRun2 = this.filesToRun.find(
          (document) => document.documentNumber === 4
        )?.file;

        this.agentFiveService
          .postDocumentsToCYvsPY([fileToRun1, fileToRun2])
          .pipe(
            take(1),
            tap((_) => {
              // activate the button for the check after posting the document
              if (check) {
                const ch = {
                  name: check.name,
                  checkNumber: check.checkNumber,
                  runLoading: true,
                };
                this.isThereALatestRun(check);
              }
            })
          )
          .subscribe({
            next: (data: any) => {
              check.errorMessage = '';
              this.toastService.showToast(
                'success',
                `${check.name} processing initialized successfully.`,
                {
                  classTheme: '',
                  autoHide: false,
                }
              );
              setTimeout(() => {
                this.isThereALatestRun(check);
              }, 30000);
            },
            error: (error: HttpErrorResponse) => {
              if (check.reportId !== undefined) {
                check.disabled = false;
              }
              check.runLoading = false;

              if (!fileToRun1 && !fileToRun2) {
                check.errorMessage = `Test failed: Missing Current Year Tax Computation and Prior Year Tax Computation files.`;
              } else if (!fileToRun1 && fileToRun2) {
                check.errorMessage = `Test failed: Missing Current Year Tax Computation file.`;
              } else if (fileToRun1 && !fileToRun2) {
                check.errorMessage = `Test failed: Missing Prior Year Tax Computation file.`;
              } else if (fileToRun1 && fileToRun2) {
                const message: string = error.error.message;
                this.toastService.showToast('error', `${message}`, {
                  classTheme: '',
                  autoHide: false,
                });
              }
            },
          });
        break;
      }
    }
  }

  checkForExistingRuns() {
    if (
      this.warningForFSChecks ||
      this.warningForFSvsComp ||
      this.warningForCYvsPY
    ) {
      //display toast
      this.toastService
        .showToast('warning', 'You will lost data from past run checks', {
          classTheme: 'motif-theme-light',
          content:
            "If you continue with this process of uploding new data, past data and previous runs or checks will be lost. You won't be able to return to the session where you last left off.",
          actionText: 'Ok',
          autoHide: false,
        })
        .then((action) => {
          if (action) {
            this.runAllChecks();
          }
        });
    } else {
      this.runAllChecks();
    }
  }

  private runAllChecks() {
    const delayPerCheck: number = 2000; // 2 seconds

    //Apply dark filter to each element
    this.checksArray.forEach(
      (check: CheckCard) => (check.darkenCardContent = true)
    );

    this.checksArray.forEach((check: CheckCard, index: number) => {
      setTimeout(() => {
        // remove the dark filter
        check.darkenCardContent = false;
        // then run the check
        this.runCheck(check.checkNumber);
      }, delayPerCheck * (index + 1));
    });
  }

  isRunDisabled(checkNumber: number): boolean {
    const check = this.checksArray.find((c) => c.checkNumber === checkNumber);
    if (check?.runLoading) return true;

    const hasDoc = (docNum: number) =>
      !!this.filesToRun.find((d) => d.documentNumber === docNum)?.file;

    switch (checkNumber) {
      case 1:
        return !hasDoc(1);
      case 2:
        return !hasDoc(1) || !hasDoc(3);
      case 3:
        return !hasDoc(1) || !hasDoc(3) || !hasDoc(4);
      default:
        return true;
    }
  }

  checkForRunAllEnable(): boolean {
    const isAnyCheckRunning = this.checksArray.some(
      (check) => check.runLoading
    );
    const isAnyCheckIncomplete = this.checksArray.some(
      (check) => !check.latestRunCheckCompleted
    );

    if (!isAnyCheckIncomplete) {
      this.retrievedFilesEmitter.emit(this.retrievedFiles);
    }

    const hasDoc = (docNum: number) =>
      !!this.filesToRun.find((d: any) => d.documentNumber === docNum)?.file;
    const allDocsUploaded = hasDoc(1) && hasDoc(3) && hasDoc(4);

    return isAnyCheckRunning || isAnyCheckIncomplete || !allDocsUploaded;
  }

  private getLatestProjectId(data: any) {
    return data?.items[0]?.id;
  }

  private getLatestProjectStatus(data: any) {
    return data?.items[0]?.processes[0]?.status;
  }

  private getFiles(data: any): ApiFile[] {
    const uploaded_date = data?.items[0]?.processes[0]?.created_at;
    let files = data?.items[0]?.processes[0]?.files;

    return files?.map((file: ApiFile) => {
      return { ...file, uploadedDate: uploaded_date };
    });
  }

  // logic to add files to the array of retrieved files
  private manageFilesToRetrieve(files: ApiFile[]): void {
    files?.forEach((file: ApiFile | any) => {
      const alreadyExistingFile: ApiFile | any = this.retrievedFiles.find(
        (existingFile: ApiFile | any) => existingFile.type === file.type
      );

      if (alreadyExistingFile) {
        const alreadyExistingDate = new Date(alreadyExistingFile.uploadedDate);
        const fileDate = new Date(file.uploadedDate);

        if (fileDate > alreadyExistingDate) {
          const index = this.retrievedFiles.findIndex(
            (existingFile: ApiFile | any) => existingFile.type === file.type
          );

          if (index !== -1) {
            this.retrievedFiles[index] = file;
          }
        }
      } else {
        this.retrievedFiles.push(file);
      }
    });

    this.retrievedFiles.sort((a: ApiFile, b: ApiFile) => a.type! - b.type!);
  }

  private checkErrorMessageOnUpload(check: CheckCard) {
    switch (check.checkNumber) {
      case 1: {
        // FS
        const fileToRun = this.filesToRun.find(
          (document) => document.documentNumber === 1
        )?.file;

        if (fileToRun) {
          check.errorMessage = '';
        }
        break;
      }
      case 2: {
        // FS vs Comp
        const fileToRun1 = this.filesToRun.find(
          (document) => document.documentNumber === 1
        )?.file;
        const fileToRun2 = this.filesToRun.find(
          (document) => document.documentNumber === 3
        )?.file;

        if (!fileToRun1 && fileToRun2) {
          check.errorMessage = `Test failed: Missing Current Year Financial Statements file.`;
        } else if (fileToRun1 && !fileToRun2) {
          check.errorMessage = `Test failed: Missing Current Year Tax Computation file.`;
        } else if (fileToRun1 && fileToRun2) {
          check.errorMessage = '';
        }
        break;
      }
      case 3: {
        // CY vs PY Comp
        const fileToRun1 = this.filesToRun.find(
          (document) => document.documentNumber === 3
        )?.file;
        const fileToRun2 = this.filesToRun.find(
          (document) => document.documentNumber === 4
        )?.file;

        if (!fileToRun1 && fileToRun2) {
          check.errorMessage = `Test failed: Missing Current Year Tax Computation file.`;
        } else if (fileToRun1 && !fileToRun2) {
          check.errorMessage = `Test failed: Missing Prior Year Tax Computation file.`;
        } else if (fileToRun1 && fileToRun2) {
          check.errorMessage = '';
        }
        break;
      }
    }
  }
}
